/* eslint-disable functional/prefer-readonly-type */
import React, { useEffect, useState } from 'react';
import { Autocomplete, FormControl, TextField, Tooltip, InputAdornment } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLang } from '~/hooks/useLang';
import { useHookstate } from '@hookstate/core';
import { AutosResponse } from './interface';
import { getVehiclesDispatch } from './service';

interface AutoProps {
    readonly onAutosChange: (value: number[]) => void;
    readonly autosId?: readonly number[];
    readonly disabled?: boolean
    readonly dispatchId: string | number | null;
    readonly externalOptions?: readonly AutosResponse[];
}

export default function FilterAutocompleteAutos({ onAutosChange, autosId, dispatchId, externalOptions }: AutoProps) {
    const { translate } = useLang();
    const [options, setOptions] = useState<readonly AutosResponse[]>([]);
    const [value, setValue] = useState<AutosResponse[]>([]);
    const loading = useHookstate(false);

    // Sincroniza opções vindas do componente pai (evita dupla requisição)
    useEffect(() => {
        if (externalOptions !== undefined) {
            setOptions(externalOptions)
        }
    }, [externalOptions])

    // Busca interna usada apenas quando não há opções externas
    useEffect(() => {
        if (externalOptions !== undefined) return
        getVehiclesDispatch(dispatchId).then(response => {
            setOptions(response?.data ?? [])
            setValue([])
        })
    }, [dispatchId])

    return (
        <FormControl fullWidth size='small' >
            <Autocomplete
                filterSelectedOptions
                disableCloseOnSelect
                limitTags={2}
                sx={{ height: 1 }}
                ListboxProps={{ style: { maxHeight: '15rem' } }}
                id='filter-autos'
                multiple
                options={options}
                value={value}
                noOptionsText={translate('No options')}
                clearText={translate('Clear')}
                size='small'
                loading={loading.get()}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option: AutosResponse) => {
                    if (!option) return '';
                    const name = option.name || '';
                    const task = option.device_log?.task ? ` - [${option.device_log.task}]` : '';
                    const distance = option.distance_from_occurrence_in_km == null
                        ? ''
                        : ` - [a ${option.distance_from_occurrence_in_km.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} km]`;
                    return `${name}${task}${distance}`;
                }}
                onChange={(_, selected) => {
                    setValue(selected)
                    onAutosChange(selected.map(item => item.id))
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={translate('Add Vehicle')}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {params.InputProps.endAdornment}
                                    <InputAdornment position="end">
                                        <Tooltip title="Os veículos estão listados por proximidade do local da ocorrência" arrow>
                                            <InfoOutlinedIcon fontSize="small" sx={{ cursor: 'pointer', color: 'action.active' }} />
                                        </Tooltip>
                                    </InputAdornment>
                                </>
                            ),
                        }}
                    />
                )}
            />
        </FormControl>
    )
}